import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, TrendingUp, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Premium, non-punitive monthly usage meter.
 * - Renders nothing while loading (no skeleton flicker on dashboards).
 * - States: ok | warning (≥soft) | critical (≥second_soft) | exhausted (≥100%)
 * - Hides the bar entirely for unlimited plans (admin / beta).
 */
export default function UsageMeter({ compact = false, className = "" }) {
  const [panel, setPanel] = useState(null);
  useEffect(() => {
    api.get("/usage/panel").then((r) => setPanel(r.data)).catch(() => setPanel(null));
  }, []);
  if (!panel) return null;

  const { units, plan, profiles } = panel;
  const unlimited = (units?.limit || 0) <= 0;

  const stateColor =
    units.state === "exhausted" ? "bg-mauve text-ivory" :
    units.state === "critical"  ? "bg-mauve/80 text-ivory" :
    units.state === "warning"   ? "bg-champagne text-cocoa" :
    "bg-sage/80 text-ivory";

  const barFill =
    units.state === "exhausted" || units.state === "critical" ? "bg-mauve" :
    units.state === "warning"   ? "bg-champagne" :
    "bg-sage";

  const friendly =
    units.state === "exhausted" ? "Capacity reached this month" :
    units.state === "critical"  ? "Nearly at your monthly capacity" :
    units.state === "warning"   ? "You're using SugarSense deeply this month" :
    "Plenty of capacity this month";

  if (compact) {
    return (
      <div data-testid="usage-meter-compact" className={`ss-card p-4 ${className}`}>
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="text-[10px] tracking-[0.4em] uppercase text-mauve">Advisor capacity</p>
          <span className={`text-[10px] tracking-[0.25em] uppercase px-2 py-0.5 rounded-full ${stateColor}`}>{plan.name}</span>
        </div>
        {!unlimited ? (
          <>
            <div className="h-1.5 rounded-full bg-champagne overflow-hidden">
              <div className={`h-full ${barFill}`} style={{ width: `${Math.min(100, units.pct)}%` }} />
            </div>
            <div className="flex items-baseline justify-between mt-2">
              <p className="text-[12px] text-cocoa/70">
                <span className="font-medium text-cocoa">{units.used}</span> of <span className="text-cocoa">{units.limit}</span> AI Insights used
              </p>
              {units.state !== "ok" && plan.key !== "luxe" && (
                <Link to="/billing" data-testid="usage-meter-upgrade-link" className="text-[11px] text-mauve hover:text-cocoa underline">Upgrade →</Link>
              )}
            </div>
          </>
        ) : (
          <p className="text-[12px] text-cocoa/65 italic">Unlimited advisor capacity active.</p>
        )}
      </div>
    );
  }

  return (
    <div data-testid="usage-meter" className={`ss-card-premium p-6 ${className}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.4em] uppercase text-mauve">Your plan</p>
          <h3 className="font-heading text-2xl text-cocoa mt-1">{plan.name}</h3>
          <p className="text-[13px] text-cocoa/65 mt-1">{plan.tagline}</p>
        </div>
        <Link to="/billing" data-testid="usage-meter-manage-link" className="ss-pill h-9 text-xs">Manage plan <ChevronRight size={12} /></Link>
      </div>

      {!unlimited && (
        <>
          <div className="mt-5">
            <div className="flex items-baseline justify-between mb-1.5">
              <p className="text-[11px] tracking-[0.3em] uppercase text-cocoa/55">Advisor capacity</p>
              <p className="text-[12px] text-cocoa/75 tabular-nums"><span className="font-medium text-cocoa">{units.used}</span> / {units.limit}</p>
            </div>
            <div className="h-2 rounded-full bg-champagne overflow-hidden">
              <div className={`h-full transition-all duration-700 ${barFill}`} style={{ width: `${Math.min(100, units.pct)}%` }} />
            </div>
            <p className="text-[12px] text-cocoa/65 mt-2 italic">{friendly}.</p>
          </div>

          {units.state !== "ok" && plan.key !== "luxe" && (
            <div className="mt-4 ss-card p-4 bg-champagne/40">
              <div className="flex items-start gap-3">
                <TrendingUp size={16} className="text-mauve mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-[13px] text-cocoa leading-relaxed">
                    Your relationship tracking is becoming more detailed. A higher plan gives you more advisor capacity, deeper memory, and richer reports.
                  </p>
                  <Link to="/billing" data-testid="usage-meter-upgrade-cta" className="inline-flex items-center gap-1 mt-2 text-[12px] text-mauve hover:text-cocoa underline">
                    <Sparkles size={11} /> See upgrade options
                  </Link>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className="grid grid-cols-2 gap-3 mt-5">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">Profiles</p>
          <p className="text-cocoa text-sm mt-1">
            <span className="font-medium">{profiles.used}</span> of {profiles.limit >= 9999 ? "∞" : profiles.limit}
          </p>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">Deep memory</p>
          <p className="text-cocoa text-sm mt-1">{plan.deep_memory_enabled ? "On" : "Lite"}</p>
        </div>
      </div>
    </div>
  );
}
